import React, { useState, useEffect } from 'react';
import apiService, { TWRPerformance as TWRPerformanceData, PerformanceComparison } from '../services/api';

interface TWRPerformanceProps {
  className?: string;
  isAdmin?: boolean;
  userId?: string;
}

const TWRPerformanceBloomberg: React.FC<TWRPerformanceProps> = ({ className = '', isAdmin = false, userId }) => {
  const [twrData, setTwrData] = useState<TWRPerformanceData | null>(null);
  const [comparison, setComparison] = useState<PerformanceComparison | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    fetchTWRData();
  }, [userId]);

  const fetchTWRData = async () => {
    try {
      setLoading(true);
      setError(null);

      let twrResponse: TWRPerformanceData;
      let comparisonResponse: PerformanceComparison;

      if (isAdmin && userId) {
        twrResponse = await apiService.getAdminUserTWR(userId);
      } else if (isAdmin && !userId) {
        twrResponse = await apiService.getConsolidatedTWR();
      } else {
        twrResponse = await apiService.getTWRPerformance();
      }

      // Only fetch comparison for non-admin users viewing their own data
      if (!isAdmin) {
        try {
          comparisonResponse = await apiService.getPerformanceComparison();
          setComparison(comparisonResponse);
        } catch (err) {
          console.log('Comparison data not available');
          setComparison(null);
        }
      }

      setTwrData(twrResponse);
    } catch (err) {
      console.error('Error fetching TWR data:', err);
      setError('Failed to load TWR performance data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number): string => {
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(value));
    return `$${formatted}`;
  };

  const formatPercent = (value: number): string => {
    const formatted = Math.abs(value).toFixed(2);
    return `${value >= 0 ? '+' : '-'}${formatted}%`;
  };

  const MetricCard = ({
    label,
    value,
    isPercent = false,
    isPositive = true,
    showSign = false
  }: {
    label: string;
    value: number | string;
    isPercent?: boolean;
    isPositive?: boolean;
    showSign?: boolean;
  }) => (
    <div className="bb-data-cell" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
      <div className="bb-data-label" style={{ marginBottom: '4px' }}>{label}</div>
      <div className={`bb-data-value mono-number ${
        showSign ? (isPositive ? 'bb-value-positive' : 'bb-value-negative') : 'bb-value-neutral'
      }`}>
        {typeof value === 'number' ?
          (isPercent ? formatPercent(value) : formatCurrency(value)) :
          value
        }
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={`bb-panel bloomberg-terminal ${className}`}>
        <div className="bb-panel-header">TIME-WEIGHTED RETURNS</div>
        <div className="bb-panel-content" style={{ textAlign: 'center', padding: '40px' }}>
          <span className="bb-blink" style={{ color: 'var(--bb-amber)' }}>LOADING...</span>
        </div>
      </div>
    );
  }

  if (error || !twrData) {
    return (
      <div className={`bb-panel bloomberg-terminal ${className}`}>
        <div className="bb-panel-header">TIME-WEIGHTED RETURNS</div>
        <div className="bb-panel-content" style={{ textAlign: 'center', padding: '40px' }}>
          <span style={{ color: 'var(--bb-red)' }}>{error || 'NO TWR DATA AVAILABLE'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bb-panel ${className}`}>
      <div className="bb-header">
        <div className="bb-header-title">
          TIME-WEIGHTED RETURNS
          <span className="bb-tooltip" style={{ marginLeft: '8px' }}>
            <span style={{ color: 'var(--bb-text-secondary)', fontSize: '10px', cursor: 'help' }}>[?]</span>
            <span className="bb-tooltip-content">
              TWR measures true trading performance by excluding deposits and withdrawals
            </span>
          </span>
        </div>
        <button
          onClick={() => setShowComparison(!showComparison)}
          className="bb-button"
          style={{ padding: '4px 8px', fontSize: '10px' }}
        >
          {showComparison ? 'HIDE' : 'SHOW'} COMPARISON
        </button>
      </div>

      <div className="bb-panel-content" style={{ padding: 0 }}>
        {/* Main Metrics */}
        <div className="bb-data-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          <MetricCard
            label="PORTFOLIO VALUE"
            value={twrData.portfolio_value}
            isPositive={true}
          />
          <MetricCard
            label="NET INVESTED"
            value={twrData.net_invested}
            isPositive={true}
          />
          <MetricCard
            label="TRADING P&L"
            value={twrData.trading_pnl}
            isPositive={twrData.trading_pnl >= 0}
            showSign={true}
          />
          <MetricCard
            label="TOTAL TWR"
            value={twrData.twr.total}
            isPercent={true}
            isPositive={twrData.twr.total >= 0}
            showSign={true}
          />
        </div>

        <hr className="bb-separator" />

        {/* Cash Flow Details */}
        <div className="bb-data-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', backgroundColor: 'var(--bb-bg-secondary)' }}>
          <div className="bb-data-cell">
            <span className="bb-data-label">DEPOSITS:</span>
            <span className="bb-data-value mono-number bb-cash-flow-deposit">
              {formatCurrency(twrData.total_deposits)}
            </span>
          </div>
          <div className="bb-data-cell">
            <span className="bb-data-label">WITHDRAWALS:</span>
            <span className="bb-data-value mono-number bb-cash-flow-withdrawal">
              {formatCurrency(twrData.total_withdrawals)}
            </span>
          </div>
          <div className="bb-data-cell">
            <span className="bb-data-label">ROI:</span>
            <span className={`bb-data-value mono-number ${twrData.roi_percent >= 0 ? 'bb-value-positive' : 'bb-value-negative'}`}>
              {formatPercent(twrData.roi_percent)}
            </span>
          </div>
        </div>

        <hr className="bb-separator-thick" />

        {/* Period Returns */}
        <div style={{ padding: '12px' }}>
          <div className="bb-data-label" style={{ marginBottom: '8px' }}>PERIOD RETURNS (TWR)</div>
          <div className="bb-data-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <MetricCard
              label="DAILY TWR"
              value={twrData.twr.daily}
              isPercent={true}
              isPositive={twrData.twr.daily >= 0}
              showSign={true}
            />
            <MetricCard
              label="WEEKLY TWR"
              value={twrData.twr.weekly}
              isPercent={true}
              isPositive={twrData.twr.weekly >= 0}
              showSign={true}
            />
            <MetricCard
              label="MONTHLY TWR"
              value={twrData.twr.monthly}
              isPercent={true}
              isPositive={twrData.twr.monthly >= 0}
              showSign={true}
            />
          </div>
        </div>

        {/* Comparison View */}
        {showComparison && comparison && (
          <>
            <hr className="bb-separator-thick" />
            <div style={{ padding: '12px' }}>
              <div className="bb-data-label" style={{ marginBottom: '12px' }}>ABSOLUTE VS TWR COMPARISON</div>
              <table className="bb-table" style={{ fontSize: '11px' }}>
                <thead>
                  <tr className="bb-table-header">
                    <th className="bb-table-cell">PERIOD</th>
                    <th className="bb-table-cell text-right">ABS RETURN</th>
                    <th className="bb-table-cell text-right">ABS %</th>
                    <th className="bb-table-cell text-right">TWR RETURN</th>
                    <th className="bb-table-cell text-right">TWR %</th>
                    <th className="bb-table-cell text-right">DIFF</th>
                  </tr>
                </thead>
                <tbody>
                  {(['daily', 'weekly', 'monthly', 'total'] as const).map((period) => {
                    const abs = comparison.absolute[period === 'daily' ? 'daily_change' : period === 'weekly' ? 'weekly_change' : period === 'monthly' ? 'monthly_change' : 'total_return'];
                    const absPercent = comparison.absolute[period === 'daily' ? 'daily_percent' : period === 'weekly' ? 'weekly_percent' : period === 'monthly' ? 'monthly_percent' : 'total_percent'];
                    const twr = comparison.twr[`${period}_return` as keyof typeof comparison.twr];
                    const twrPercent = comparison.twr[`${period}_percent` as keyof typeof comparison.twr];
                    const diff = twrPercent - absPercent;

                    return (
                      <tr key={period} className="bb-table-row">
                        <td className="bb-table-cell" style={{ textTransform: 'uppercase' }}>{period}</td>
                        <td className={`bb-table-cell text-right mono-number ${abs >= 0 ? 'bb-value-positive' : 'bb-value-negative'}`}>
                          {formatCurrency(abs)}
                        </td>
                        <td className={`bb-table-cell text-right mono-number ${absPercent >= 0 ? 'bb-value-positive' : 'bb-value-negative'}`}>
                          {formatPercent(absPercent)}
                        </td>
                        <td className={`bb-table-cell text-right mono-number ${twr >= 0 ? 'bb-value-positive' : 'bb-value-negative'}`}>
                          {formatCurrency(twr)}
                        </td>
                        <td className={`bb-table-cell text-right mono-number ${twrPercent >= 0 ? 'bb-value-positive' : 'bb-value-negative'}`}>
                          {formatPercent(twrPercent)}
                        </td>
                        <td className={`bb-table-cell text-right mono-number`} style={{ color: diff >= 0 ? 'var(--bb-blue)' : 'var(--bb-withdrawal)' }}>
                          {formatPercent(diff)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Cash Flow Summary */}
              <div style={{
                marginTop: '12px',
                padding: '8px',
                backgroundColor: 'var(--bb-bg-secondary)',
                border: '1px solid var(--bb-border)',
                fontSize: '11px'
              }}>
                <div style={{ color: 'var(--bb-text-secondary)' }}>
                  <span style={{ color: 'var(--bb-amber)', fontWeight: 'bold' }}>CASH FLOW IMPACT:</span>
                  <div style={{ marginTop: '4px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    <div>
                      <span style={{ color: 'var(--bb-text-secondary)' }}>DEPOSITS: </span>
                      <span className="mono-number bb-cash-flow-deposit">{formatCurrency(comparison.cash_flows.total_deposits)}</span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--bb-text-secondary)' }}>WITHDRAWALS: </span>
                      <span className="mono-number bb-cash-flow-withdrawal">{formatCurrency(comparison.cash_flows.total_withdrawals)}</span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--bb-text-secondary)' }}>NET FLOW: </span>
                      <span className={`mono-number ${comparison.cash_flows.net_flow >= 0 ? 'bb-value-positive' : 'bb-value-negative'}`}>
                        {formatCurrency(comparison.cash_flows.net_flow)}
                      </span>
                    </div>
                  </div>
                  {comparison.cash_flows.last_flow_date && (
                    <div style={{ marginTop: '4px' }}>
                      <span style={{ color: 'var(--bb-text-secondary)' }}>LAST FLOW: </span>
                      <span style={{ color: 'var(--bb-text-data)' }}>
                        {new Date(comparison.cash_flows.last_flow_date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: '2-digit',
                          year: 'numeric'
                        }).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Understanding TWR */}
      <div style={{
        padding: '8px 12px',
        backgroundColor: 'var(--bb-bg-secondary)',
        borderTop: '1px solid var(--bb-border)',
        fontSize: '10px',
        color: 'var(--bb-text-secondary)'
      }}>
        <strong style={{ color: 'var(--bb-amber)' }}>UNDERSTANDING TIME-WEIGHTED RETURNS:</strong>
        <span style={{ marginLeft: '8px' }}>
          TWR MEASURES YOUR TRUE TRADING PERFORMANCE BY REMOVING THE IMPACT OF DEPOSITS AND WITHDRAWALS.
          THIS GIVES YOU AN ACCURATE VIEW OF HOW WELL YOUR TRADING STRATEGIES ARE PERFORMING,
          INDEPENDENT OF HOW MUCH CAPITAL YOU'VE ADDED OR REMOVED FROM YOUR ACCOUNT.
        </span>
      </div>
    </div>
  );
};

export default TWRPerformanceBloomberg;